import React from 'react';
import { Area, XAxis, YAxis, ResponsiveContainer, ComposedChart } from 'recharts';
import type { ElevationPoint } from '../../types';
import type { ClimbSegment } from '../../utils/climbDetection';

/**
 * Get color based on gradient/steepness percentage
 * Matches the main elevation chart color scheme
 */
const getGradientColor = (gradient: number): string => {
  // Match the main elevation chart thresholds exactly
  if (gradient < 4) return '#4a7c2a';      // Green for < 4%
  if (gradient < 8) return '#ff9800';      // Orange for 4-8%
  if (gradient < 12) return '#ff0000';     // Red for 8-12%
  return '#8B0000';                        // Dark red for >= 12%
};

interface ClimbElevationChartProps {
  climb: ClimbSegment;
  elevationData: ElevationPoint[];
  width?: number;
  height?: number;
}

interface ElevationPointWithGradient extends ElevationPoint {
  normalizedDistance: number;
  gradient: number;
  gradientColor: string;
}

/**
 * Small elevation chart component for individual climbs
 * Shows the elevation profile with gradient-based color coding
 */
export const ClimbElevationChart: React.FC<ClimbElevationChartProps> = ({
  climb,
  elevationData,
  width = 120,
  height = 60
}) => {
  // Extract and process elevation points for this climb
  const climbElevationPoints = React.useMemo(() => {
    // Use the climb's own elevation profile if available
    // Fall back to filtering from route elevation data
    let climbPoints = climb.elevationProfile && climb.elevationProfile.length > 0
      ? climb.elevationProfile
      : elevationData.filter(point => 
          point.distance >= climb.startDistance && point.distance <= climb.endDistance
        );
    
    if (climbPoints.length === 0) return [];
    
    // DON'T aggregate before gradient calculation - use original steepness values
    // This matches how the main elevation chart works (uses pre-calculated steepness)
    
    // Normalize distances and use original steepness values
    const startDistance = climbPoints[0].distance;
    const processedPoints: ElevationPointWithGradient[] = climbPoints.map(point => {
      const normalizedDistance = (point.distance - startDistance) * 1000; // Convert to meters
      
      // Use steepness if available (pre-calculated), otherwise calculate between consecutive points
      const gradient = point.steepness !== undefined ? Math.abs(point.steepness) : 0;
      
      return {
        ...point,
        normalizedDistance,
        gradient,
        gradientColor: getGradientColor(gradient)
      };
    });
    
    return processedPoints;
  }, [climb, elevationData]);

  if (climbElevationPoints.length === 0) {
    return (
      <div 
        style={{ 
          width, 
          height, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#666'
        }}
      >
        No data
      </div>
    );
  }

  // Calculate min/max for better scaling
  const elevations = climbElevationPoints.map(p => p.elevation);
  const minElevation = Math.min(...elevations);
  const maxElevation = Math.max(...elevations);
  const elevationRange = maxElevation - minElevation;
  
  // Add some padding to the elevation range for better visualization
  const paddingPercent = 0.1; // 10% padding
  const padding = elevationRange * paddingPercent;
  const chartMinElevation = minElevation - padding;
  const chartMaxElevation = maxElevation + padding;

  // Create gradient segments for coloring with aggregation (matching main chart logic)
  const gradientSegments = React.useMemo(() => {
    if (climbElevationPoints.length < 2) return [];
    
    const segments = [];
    
    // Calculate total distance of the climb (in meters)
    const totalDistance = climbElevationPoints[climbElevationPoints.length - 1].normalizedDistance - 
                         climbElevationPoints[0].normalizedDistance;
    
    // Match the main elevation chart's segment size: 150m minimum (0.15 km)
    // This ensures climb thumbnails show the same level of detail as the main chart when zoomed
    const minSegmentSizeMeters = 150; // 150m to match main chart (0.15 km)
    const targetSegmentCount = Math.max(3, Math.ceil(totalDistance / minSegmentSizeMeters));
    const segmentSize = totalDistance / targetSegmentCount;
    

    
    // Group points into segments and calculate average gradient
    for (let segmentIdx = 0; segmentIdx < targetSegmentCount; segmentIdx++) {
      const segmentStart = climbElevationPoints[0].normalizedDistance + (segmentIdx * segmentSize);
      const segmentEnd = segmentStart + segmentSize;
      
      // Find points in this segment
      const segmentPoints = climbElevationPoints.filter(p => 
        p.normalizedDistance >= segmentStart && p.normalizedDistance < segmentEnd
      );
      
      if (segmentPoints.length > 0) {
        // Calculate average gradient for this segment
        const avgGradient = segmentPoints.reduce((sum, p) => sum + p.gradient, 0) / segmentPoints.length;
        const color = getGradientColor(avgGradient);
        
        segments.push({
          data: segmentPoints,
          color: color,
          gradient: avgGradient
        });
      }
    }
    
    return segments;
  }, [climbElevationPoints]);

  // Create gradient ID for this specific chart instance
  const gradientId = `climb-gradient-${climb.startDistance}-${climb.endDistance}`;

  return (
    <div style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={climbElevationPoints}
          margin={{ top: 2, right: 2, left: 2, bottom: 2 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              {gradientSegments.map((segment, index) => {
                // Calculate offset as percentage of total distance
                const totalDistance = climbElevationPoints[climbElevationPoints.length - 1].normalizedDistance - 
                                     climbElevationPoints[0].normalizedDistance;
                const segmentMidpoint = (segment.data[0].normalizedDistance + 
                                        segment.data[segment.data.length - 1].normalizedDistance) / 2;
                const offset = totalDistance > 0 
                  ? ((segmentMidpoint - climbElevationPoints[0].normalizedDistance) / totalDistance) * 100
                  : 0;
                
                return (
                  <stop
                    key={index}
                    offset={`${Math.max(0, Math.min(100, offset))}%`}
                    stopColor={segment.color}
                  />
                );
              })}
            </linearGradient>
          </defs>
          
          <XAxis 
            dataKey="normalizedDistance"
            hide={true}
            type="number"
            scale="linear"
            domain={['dataMin', 'dataMax']}
          />
          <YAxis 
            hide={true}
            type="number"
            domain={[chartMinElevation, chartMaxElevation]}
          />
          
          {/* Single area with gradient coloring */}
          <Area
            data={climbElevationPoints}
            type="monotone"
            dataKey="elevation"
            stroke={`url(#${gradientId})`}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            fillOpacity={0.3}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};